/**
 * Join-mapping integrity for the Semantic Designer.
 *
 * A `field_mapping` join references fields by id. Two things used to leave
 * those references pointing at nothing, and both were invisible (FIXES.md
 * 2026-10-06): the import re-minted field ids without re-pointing joins, and
 * removing a field left every join row that referenced it in place. A select
 * whose value matches no option then displays the FIRST field, so the stale
 * row read as "PCN = PCN" and looked correct. The hub now refuses to serve an
 * id, but the Designer is where a person has to be able to SEE one.
 */

export type MappingField = { id: string; name: string };
export type MappingRow = { source_field: string; target_field: string };

export type MappingObject = { id: string; name: string; fields: MappingField[] };
export type MappingRelationship = {
  id: string;
  name: string;
  source_object_id: string;
  target_object_id: string;
  join_condition: { type: "field_mapping"; mappings: MappingRow[] } | { type: "expression"; expression: string };
};
/** ADR-0212: an area's placement may restrict columns of the placed object, by field id. */
export type RestrictionPlacement = { object_id: string; restricted_field_ids?: string[] };
export type RestrictionArea = { object_placements: RestrictionPlacement[] };
export type MappingSpec<O extends MappingObject, R extends MappingRelationship> = {
  objects: O[];
  relationships: R[];
  subject_areas?: RestrictionArea[];
};

/** True when `fieldId` names no field in `fields` — a stale reference. */
export function isDanglingFieldRef(fields: readonly MappingField[], fieldId: string): boolean {
  return !fields.some((f) => f.id === fieldId);
}

/**
 * Remove one field from its object AND every join row that references it,
 * the way deleting an object already removes its relationships. A
 * field_mapping relationship left with no rows is removed too: a join with no
 * condition is not a join, and the hub would not serve it anyway. The field
 * also leaves every subject area's restriction list (ADR-0212 §3.3) — the hub
 * refuses a restriction naming a field the object no longer has.
 *
 * Returns the next spec (the input is not mutated) and what else went with
 * the field, so the caller can say so.
 */
export function removeFieldFromSpec<O extends MappingObject, R extends MappingRelationship, S extends MappingSpec<O, R>>(
  spec: S,
  objectId: string,
  fieldId: string
): { spec: S; removedRows: number; removedRelationships: string[] } {
  const next = JSON.parse(JSON.stringify(spec)) as S;
  const obj = next.objects.find((o) => o.id === objectId);
  if (!obj) return { spec: next, removedRows: 0, removedRelationships: [] };
  const before = obj.fields.length;
  obj.fields = obj.fields.filter((f) => f.id !== fieldId);
  if (obj.fields.length === before) return { spec: next, removedRows: 0, removedRelationships: [] };

  let removedRows = 0;
  const removedRelationships: string[] = [];
  next.relationships = next.relationships.filter((r) => {
    if (r.join_condition.type !== "field_mapping") return true;
    const touchesSource = r.source_object_id === objectId;
    const touchesTarget = r.target_object_id === objectId;
    if (!touchesSource && !touchesTarget) return true;
    const kept = r.join_condition.mappings.filter(
      (m) => !((touchesSource && m.source_field === fieldId) || (touchesTarget && m.target_field === fieldId))
    );
    removedRows += r.join_condition.mappings.length - kept.length;
    if (kept.length === 0) {
      removedRelationships.push(r.name);
      return false;
    }
    r.join_condition.mappings = kept;
    return true;
  });
  for (const area of next.subject_areas ?? []) {
    for (const p of area.object_placements) {
      if (p.object_id !== objectId || !p.restricted_field_ids) continue;
      p.restricted_field_ids = p.restricted_field_ids.filter((id) => id !== fieldId);
      if (p.restricted_field_ids.length === 0) delete p.restricted_field_ids;
    }
  }
  return { spec: next, removedRows, removedRelationships };
}

/**
 * ADR-0212 (E4, ruling 25) — drop every area restriction that names no field
 * of the placed object. An Excel import or an infer-schema can drop a column
 * (or, before ruling 25, re-mint its id); a restriction left naming nothing
 * would be refused by the hub on the next save. A pruned restriction is a
 * restriction cleared, and the hub ledgers it as one.
 *
 * Returns the next spec (the input is not mutated) and how many were pruned.
 */
export function pruneRestrictionsToFields<S extends { objects: MappingObject[]; subject_areas?: RestrictionArea[] }>(
  spec: S
): { spec: S; pruned: number } {
  const next = JSON.parse(JSON.stringify(spec)) as S;
  const fieldsOf = new Map(next.objects.map((o) => [o.id, new Set(o.fields.map((f) => f.id))] as const));
  let pruned = 0;
  for (const area of next.subject_areas ?? []) {
    for (const p of area.object_placements) {
      if (!p.restricted_field_ids) continue;
      const fields = fieldsOf.get(p.object_id);
      const kept = p.restricted_field_ids.filter((id) => fields?.has(id) === true);
      pruned += p.restricted_field_ids.length - kept.length;
      if (kept.length === 0) delete p.restricted_field_ids;
      else p.restricted_field_ids = kept;
    }
  }
  return { spec: next, pruned };
}
